type ClerkError = {
  errors?: Array<{ code?: string; message?: string; longMessage?: string }>;
};

export type AuthErrorContext =
  | "sign-in"
  | "sign-in-factor"
  | "sign-up"
  | "sign-up-verification"
  | "reset"
  | "reset-verification";

function clerkErrorCode(error: unknown): string {
  const value = error as ClerkError;
  return value?.errors?.[0]?.code ?? "";
}

export function isAlreadyVerified(error: unknown): boolean {
  return ["verification_already_verified", "form_code_already_verified"].includes(
    clerkErrorCode(error),
  );
}

/** Never expose Clerk's English errors in the authentication UI. */
export function authErrorMessage(error: unknown, context: AuthErrorContext): string {
  const code = clerkErrorCode(error);

  if (code === "form_identifier_not_found" && context === "sign-in") {
    return "Não foi possível localizar uma conta ativa para este email. Confira o email informado ou aguarde um convite ou a liberação de acesso.";
  }
  if (["form_identifier_exists", "form_identifier_exists_in_a_different_account"].includes(code)) {
    return "Já existe uma conta para este email. Entre ou recupere sua senha.";
  }
  if (["form_code_incorrect", "verification_failed", "form_code_invalid"].includes(code)) {
    return "Código inválido. Confira o código recebido e tente novamente.";
  }
  if (["verification_expired", "form_code_expired"].includes(code)) {
    return "Este código expirou. Solicite um novo código para continuar.";
  }
  if (isAlreadyVerified(error)) {
    return "Este código já foi confirmado. Tente entrar com seu email.";
  }
  if (code === "form_password_incorrect") {
    return "Senha incorreta. Tente novamente ou redefina sua senha.";
  }
  if (["form_param_format_invalid", "form_param_nil"].includes(code)) {
    return "Confira os dados informados e tente novamente.";
  }

  switch (context) {
    case "sign-in":
      return "Não foi possível verificar sua conta agora. Tente novamente em instantes.";
    case "sign-in-factor":
      return "Não foi possível confirmar seus dados de acesso. Tente novamente.";
    case "sign-up":
      return "Não foi possível criar sua conta. Confira os dados e tente novamente.";
    case "sign-up-verification":
      return "Não foi possível confirmar o código. Tente novamente.";
    case "reset":
      return "Não foi possível iniciar a redefinição de senha.";
    case "reset-verification":
      return "Não foi possível redefinir sua senha. Confira o código e tente novamente.";
  }

  return "Não foi possível concluir esta ação. Tente novamente.";
}
